import { useEffect, useState } from 'react';
import { TimeTrackerCard } from '@/components/TimeTrackerCard';
import { WorkingStatsCards } from '@/components/WorkingStatsCards';
import { RecentRecordsCard } from '@/components/RecentRecordsCard';
import { useAuth } from '@/hooks/useAuth';
import { useClients } from '@/hooks/useClients';
import { useProjects } from '@/hooks/useProjects';
import { useTimeTracker } from '@/hooks/useTimeTracker';
import { fixTotalMinutes } from '@/lib/timeTracking';

export const HomePage = () => {
  const { user, loading } = useAuth();
  const { clients } = useClients();
  const { projects } = useProjects('active');
  const { recentRecords } = useTimeTracker();
  const [isFixing, setIsFixing] = useState(false);

  // 今月の稼働日数を計算
  const thisMonthWorkingDays = (() => {
    const now = new Date();
    const thisMonth = now.getMonth();
    const thisYear = now.getFullYear();
    
    // 今月のレコードから重複しない日付を抽出
    const uniqueDays = new Set<string>();
    recentRecords.forEach((record) => {
      if (record.clockInTime) {
        const recordDate = record.clockInTime.toDate();
        if (recordDate.getMonth() === thisMonth && recordDate.getFullYear() === thisYear) {
          // YYYY-MM-DD形式で日付をユニークに管理
          const dateKey = `${recordDate.getFullYear()}-${recordDate.getMonth()}-${recordDate.getDate()}`;
          uniqueDays.add(dateKey);
        }
      }
    });
    
    return uniqueDays.size;
  })();

  // 🔧 一度だけtotalMinutesを修正
  useEffect(() => {
    if (user?.uid) {
      const hasRun = localStorage.getItem('totalMinutes_fixed_v2');
      if (!hasRun) {
        console.log('[HomePage] Running totalMinutes migration...');
        setIsFixing(true);
        fixTotalMinutes(user.uid)
          .then((count) => {
            console.log(`[HomePage] Migration completed: ${count} records fixed`);
            if (count > 0) {
              alert(`✅ ${count}件のレコードのtotalMinutesを修正しました。ページをリロードします。`);
              localStorage.setItem('totalMinutes_fixed_v2', 'true');
              window.location.reload();
            } else {
              console.log('[HomePage] No records needed fixing');
              localStorage.setItem('totalMinutes_fixed_v2', 'true');
              setIsFixing(false);
            }
          })
          .catch((error) => {
            console.error('[HomePage] Migration failed:', error);
            setIsFixing(false);
          });
      }
    }
  }, [user]);

  if (loading || isFixing) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{isFixing ? 'データを修正中...' : '読み込み中...'}</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-gray-600">ユーザー情報が取得できませんでした。</p>
          <p className="text-sm text-gray-500 mt-2">再度ログインしてください。</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-8">
      {/* Welcome Section */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary-600 via-primary-500 to-primary-400 p-8 text-white shadow-xl">
        <div className="relative z-10">
          <h1 className="text-3xl md:text-4xl font-bold mb-2">
            ようこそ、{user?.displayName}さん 👋
          </h1>
          <p className="text-primary-100 text-lg">
            今日も素晴らしい一日にしましょう！
          </p>
        </div>
        
        {/* Decorative Elements */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -mr-32 -mt-32 blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/10 rounded-full -ml-24 -mb-24 blur-3xl"></div>
      </div>

      {/* Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Time Tracker Card - Takes 2 columns on large screens */}
        <div className="lg:col-span-2">
          <TimeTrackerCard />
        </div>

        {/* Quick Stats - Takes 1 column */}
        <div className="space-y-6">
          <div className="card bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-100">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 bg-amber-500 rounded-lg">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-gray-900">クイック情報</h3>
            </div>
            
            <div className="space-y-3">
              <div className="p-3 bg-white rounded-lg">
                <div className="text-xs text-gray-600 mb-1">アクティブプロジェクト</div>
                <div className="text-2xl font-bold text-gray-900">{projects.length}</div>
              </div>
              
              <div className="p-3 bg-white rounded-lg">
                <div className="text-xs text-gray-600 mb-1">登録クライアント数</div>
                <div className="text-2xl font-bold text-gray-900">{clients.length}</div>
              </div>
              
              <div className="p-3 bg-white rounded-lg">
                <div className="text-xs text-gray-600 mb-1">今月の稼働日数</div>
                <div className="text-2xl font-bold text-gray-900">{thisMonthWorkingDays}日</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Working Stats Cards */}
      <WorkingStatsCards />

      {/* Recent Records */}
      <RecentRecordsCard />
    </div>
  );
};
